import React, { useState, useEffect, useRef } from 'react';
import { Divider, styled, Grid } from '@mui/material';
import Button from 'components/Button';
import GridContainer from 'components/GridContainer';
import PageSection from 'components/PageSection';
import Heading from 'components/Heading';
import ButtonAsText from 'components/ButtonAsText';
import { StyledNote } from '../../../Allocations/CreateAllocation/shared/StyledComponents';
import { Row } from 'components/Layout';
import { round } from 'lodash';
import { ExitDetailsFormValues } from '../ExitDetailsForm';
import { downloadShareConsiderationTemplate } from './downloadShareConsiderationTemplate';
import { ShareConsiderationAllocationMethod } from 'further-types/exit';
import type { ShareConsideration } from '../types';

const StyledUploadButton = styled(Button)(({ theme }) => ({
  width: theme.spacing(33.75),
}));

export const StyledDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(8),
  marginTop: theme.spacing(2),
}));

const StyledButtonRow = styled(Row)(({ theme }) => ({
  marginBottom: theme.spacing(6),
}));

interface UploadShareConsiderationSheetProps {
  exitDetails: ExitDetailsFormValues;
  shareConsiderations: Array<ShareConsideration>;
  setShareConsiderations: React.Dispatch<
    React.SetStateAction<Array<ShareConsideration>>
  >;
  onTemplateReset: () => void;
  onUploadComplete?: () => void;
}

const UploadShareConsiderationSheet: React.FC<
  UploadShareConsiderationSheetProps
> = ({
  exitDetails,
  shareConsiderations,
  setShareConsiderations,
  onTemplateReset,
  onUploadComplete,
}) => {
  const [uploadErrors, setUploadErrors] = useState<Array<string>>([]);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const previousAllocationMethodRef = useRef<
    ShareConsiderationAllocationMethod | undefined
  >(undefined);

  const downloadTemplate = React.useCallback(() => {
    downloadShareConsiderationTemplate({
      exitDetails,
      shareConsiderations,
      setAllSharesToZero: true,
      filename: `Share-Exit-allocation-sheet-${Date.now()}.xlsx`,
      isBeingEdited: false, // This is for new exits, so always false
    });
    setHasDownloaded(true);
  }, [exitDetails, shareConsiderations]);

  // Reset hasDownloaded when allocation method changes to Upload
  // This ensures we download the template when switching to upload method
  useEffect(() => {
    if (
      exitDetails.shareConsiderationAllocationMethod ===
        ShareConsiderationAllocationMethod.Upload &&
      previousAllocationMethodRef.current !==
        ShareConsiderationAllocationMethod.Upload
    ) {
      previousAllocationMethodRef.current =
        ShareConsiderationAllocationMethod.Upload;
      setHasDownloaded(false);
    }
  }, [exitDetails.shareConsiderationAllocationMethod]);

  // Auto-download when component mounts and shareConsiderations are available
  useEffect(() => {
    if (
      shareConsiderations.length &&
      !hasDownloaded &&
      exitDetails.shareConsiderationAllocationMethod ===
        ShareConsiderationAllocationMethod.Upload
    ) {
      downloadTemplate();
    }
  }, [
    shareConsiderations.length,
    hasDownloaded,
    downloadTemplate,
    exitDetails.shareConsiderationAllocationMethod,
  ]);

  const resetFileInput = (event) => {
    const { target = {} } = event || {};
    target.value = '';
  };

  const handleSubmitUpload = async (event) => {
    setUploadErrors([]);

    if (event.target.files?.length) {
      try {
        const ExcelJS = (await import('exceljs')).default;
        const file = event.target.files[0];
        const wb = new ExcelJS.Workbook();
        const reader = new FileReader();
        const updateValues = new Map();

        reader.readAsArrayBuffer(file);
        reader.onload = () => {
          const buffer = reader.result;
          //@ts-expect-error
          wb.xlsx.load(buffer).then((workbook) => {
            workbook.eachSheet((sheet) => {
              sheet.eachRow((row, rowIndex) => {
                // For share consideration, ignore first 7 rows (instructions + headers)
                if (rowIndex <= 7) return;

                // Column M = Shareholding ID (13th column, index 13) - this is the actual shareholdingId
                // Column G = Exit shares sold (7th column, index 7)
                // Column H = New shares allocated (8th column, index 8)
                const shareholdingIdCell = row.getCell(13);
                const exitSharesSoldCell = row.getCell(7);
                const newSharesAllocatedCell = row.getCell(8);

                const shareholdingIdValue = shareholdingIdCell.value;
                const exitSharesSoldValue = exitSharesSoldCell.value;
                const newSharesAllocatedValue = newSharesAllocatedCell.value;

                // Skip empty rows
                if (!shareholdingIdValue) return;

                // Extract the actual value (handle formula results)
                const shareholdingId = String(
                  //@ts-expect-error
                  shareholdingIdValue?.result || shareholdingIdValue || '',
                ).trim();
                const exitSharesSoldRaw =
                  //@ts-expect-error
                  exitSharesSoldValue?.result || exitSharesSoldValue;
                const newSharesAllocatedRaw =
                  //@ts-expect-error
                  newSharesAllocatedValue?.result || newSharesAllocatedValue;

                // Convert to number and round to 10 decimal places
                const exitSharesSold = round(
                  Number(exitSharesSoldRaw) || 0,
                  10,
                );
                const newSharesAllocated = round(
                  Number(newSharesAllocatedRaw) || 0,
                  10,
                );

                updateValues.set(shareholdingId, {
                  exitSharesSold,
                  newSharesAllocated,
                });
              });
            });

            if (updateValues.size) {
              // Update shareConsiderations from the file
              const updatedShareConsiderations = shareConsiderations.map(
                (sc) => {
                  const shareholdingIdKey = String(sc.shareholdingId).trim();
                  const updates = updateValues.get(shareholdingIdKey);
                  if (updates !== undefined) {
                    return {
                      ...sc,
                      sharesAllocatedForSale: updates.exitSharesSold,
                      newSharesAllocated: updates.newSharesAllocated,
                    };
                  }
                  return sc;
                },
              );
              setShareConsiderations(updatedShareConsiderations);
              setUploadErrors([]);
              // Notify parent that upload is complete
              onUploadComplete?.();
            } else {
              setUploadErrors(['No valid data found in the uploaded file.']);
            }
          });
        };
      } catch (error) {
        console.error('Upload error:', error);
        setUploadErrors([
          'Error processing the uploaded file. Please check the file format.',
        ]);
      }
    }

    resetFileInput(event);
  };

  return (
    <PageSection>
      <StyledDivider />

      <Heading variant="h2">
        Upload share consideration allocation sheet
      </Heading>

      <GridContainer>
        <Grid size={{ md: 9 }}>
          <StyledNote>
            Please check your download folder for your allocation sheet. Please
            fill in the share quantities and upload the sheet here. Form didn't
            download?{' '}
            <ButtonAsText
              variant="text"
              color="primary"
              onClick={() => {
                setHasDownloaded(false);
                onTemplateReset();
                downloadTemplate();
              }}
            >
              Click here to try again
            </ButtonAsText>
            .
          </StyledNote>

          <StyledButtonRow justify="flex-end">
            <StyledUploadButton
              variant="outlined"
              component="label"
              color="primary"
              onChange={handleSubmitUpload}
            >
              Upload
              <input name="uploadExcelFile" type="file" accept=".xlsx" hidden />
            </StyledUploadButton>
          </StyledButtonRow>

          {uploadErrors.length > 0 && (
            <div style={{ color: 'red', marginTop: '1rem' }}>
              {uploadErrors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          )}
        </Grid>
      </GridContainer>
    </PageSection>
  );
};

export default UploadShareConsiderationSheet;
